import React, { useContext, useEffect, useMemo, useState } from 'react';
import { CButton, CCol, CRow } from '@coreui/react';
import Flatpickr from "react-flatpickr";
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { flatten, isArray } from 'lodash';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { selectRemovedTechnicians, selectTechnicians } from '../../../store/selectors/user.selectors';
import DriversSelect from '../../components/DriversSelect';
import { isTodayOrFuture } from '../../../shared/utils/date-utils';
import {
  selectInsightsFilter,
  selectLoadedInsightsStatus,
  selectLoadingInsightsStatus,
} from '../../../store/selectors/insights.selectors';
import { UpdateInsightsFilter } from '../../actions/insights.action';
import { LoadUsers } from '../../../technicians/actions/userManagement.action';
import { ConfigContext } from '../../../shared/contexts/journeyConfig.context';
import { selectOrgHierarchySelectedOrg } from '../../../store/selectors/orgHierarchy.selectors';

const DATE_TIME_PICKER_OPTIONS = {
  mode: 'range',
  disable: [(date) => {
    return isTodayOrFuture(date.getTime());
  }]
};

const UNASSIGNED = {
  name: 'Unassigned',
  id: -1
};
const REMOVED_AGENTS = (labels) => ({
  name: `Removed ${labels.agent}`,
  id: []
});

interface InsightsFilterProps {
  onPrint: () => void;
  isTaskDetails: boolean;
}

const InsightsFilter = (props: InsightsFilterProps) => {
  const {
    onPrint,
    isTaskDetails
  } = props;
  const {
    enableOrgHierarchyModule
  } = useFlags();
  const history = useHistory();
  const dispatch = useDispatch();
  const labels = useContext(ConfigContext);
  const agents = useSelector(selectTechnicians) || [];
  const removedAgents = useSelector(selectRemovedTechnicians) || [];
  const org = useSelector(selectOrgHierarchySelectedOrg);
  const filter = useSelector(selectInsightsFilter);
  const isLoading = useSelector(selectLoadingInsightsStatus);
  const loaded = useSelector(selectLoadedInsightsStatus);

  const [allAgents, setAllAgents] = useState([]);

  useEffect(() => {
    if (agents?.length > 0 || removedAgents?.length > 0) {
      const result = [
        ...allAgents,
        UNASSIGNED,
        ...(agents ?? []),
        {
          ...REMOVED_AGENTS(labels),
          id: [...(removedAgents?.map((agent) => agent.id) ?? [])]
        }
      ];
      setAllAgents(result);
    }
  }, [agents, removedAgents]);


  useEffect(() => {
    if (org?._id && agents?.length === 0) {
      dispatch(new LoadUsers({
        orgId: org?._id,
        includeChildren: enableOrgHierarchyModule
      }));
    }
  }, [org]);

  useEffect(() => {
    if (allAgents?.length > 0 && org?._id && !filter) {
      dispatch(new UpdateInsightsFilter({
        orgId: org?._id,
        selectedDrivers: flatten(allAgents.map((agent) => agent.id)),
        startDate: new Date(moment().subtract(7, 'days').startOf('day').format()),
        endDate: new Date(moment().subtract(1, 'days').endOf('day').format())
      }));
    }
  }, [allAgents, org]);

  useEffect(() => {
    if (org?._id && filter && filter.orgId !== org?._id) {
      dispatch(new UpdateInsightsFilter({
        orgId: org?._id,
      }));
    }
  }, [org]);

  const handleChangeSelectedDrivers = (drivers) => {
    dispatch(new UpdateInsightsFilter({
      selectedDrivers: flatten(drivers)
    }));
  };

  const handleChangeSelectedDate = (dates) => {
    dispatch(new UpdateInsightsFilter({
      startDate: new Date(moment(dates[0]).startOf('day').format()),
      endDate: new Date(moment(dates[1]).endOf('day').format())
    }));
  };

  const navigateTo = (path: string) => {
    history.push(path);
  };

  const selectedDrivers = useMemo(() => {
    return allAgents.filter(agent => {
      if (isArray(agent.id)) {
        return agent.id?.every(id => filter?.selectedDrivers?.includes(id));
      }
      return filter?.selectedDrivers?.includes(agent.id)
    }).map((agent) => ({
      label: agent.name,
      value: agent.id
    }))
  }, [filter?.selectedDrivers]);

  return (
    <CRow className="mb-3">
      <CCol className="mb-2" md={3}>
        <Flatpickr
          id="dateRange"
          placeholder=""
          name="dateRange"
          options={ DATE_TIME_PICKER_OPTIONS }
          value={ [filter?.startDate, filter?.endDate] }
          onChange={ (value) => {
            if (value.length === 2) {
              handleChangeSelectedDate(value);
            }
          }}
          className="form-control datetimepicker h-100"
        />
      </CCol>
      <CCol className="mb-2">
        <DriversSelect
          options={allAgents.map((agent) => ({
            label: agent.name,
            value: agent.id
          }))}
          selectedDrivers={selectedDrivers}
          handleChange={(drivers) => {
            handleChangeSelectedDrivers(drivers.map(driver => driver.value))
          }}
        />
      </CCol>
      <CCol className="mb-2 text-right">
        {
          (!isLoading && loaded) ? (
            <CButton
              type="button"
              color="primary"
              className="bg-color-orange border-0 roundedBtn font-weight-xbold"
              onClick={onPrint}
            >
              Print
            </CButton>
          ) : null
        }
        {
          isTaskDetails ? (
            <CButton
              type="button"
              color="primary"
              onClick={() => navigateTo("/insights")}
              className='bg-color-orange border-0 roundedBtn font-weight-xbold ml-2'
            >
              Back
            </CButton>
          ) : null
        }
      </CCol>
    </CRow>
  );
};

export default InsightsFilter;
